import React from 'react';
import { inventoryEquipItem, inventoryFindIntersection, inventoryFindNearest, inventoryHasDuplicate } from 'logic/inventory';
import { presetItems } from 'logic/itemTypes';
import { profileCanEquip, profileEquipItem, profileEquipMerc } from 'logic/stateReducers';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import Store from 'store';
import Data from 'data';

export function useDragEx(spec, deps) {
  const result = useDrag(spec, deps);
  const dragPreview = result[2];
  React.useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, [dragPreview]);
  return result;
}

export function useDragDrop(type, dragSpecEx, dropSpecEx, deps) {
  const { collect: dragCollect, ...dragSpec }  = dragSpecEx;
  const { collect: dropCollect, ...dropSpec }  = dropSpecEx;

  const [{ isDragging, ...dragProps }, drag] = useDragEx({
    type,
    collect: monitor => Object.assign({isDragging: monitor.isDragging()}, dragCollect?.(monitor)),
    ...dragSpec,
  }, [deps]);

  const [{ isDragging2, canDrop, isOver, ...dropProps }, drop] = useDrop({
    accept: type,
    collect: monitor => Object.assign({canDrop: monitor.canDrop(), isOver: monitor.isOver(), isDragging2: monitor.getItemType() === type}, dropCollect?.(monitor)),
    ...dropSpec,
  }, [deps]);

  let className = "";
  if (isDragging) className = "d2p-dragging-self";
  if (isDragging2) className += " d2p-dragging";
  if (isOver) {
    if (canDrop) {
      className += " d2p-drop-accept";
    } else {
      className += " d2p-drop-reject";
    }
  }

  return [className, drag, drop, Object.assign({}, dragProps, dropProps)];
}

export function useItemValidator(slot, merc) {
  const store = Store.useStore();
  return React.useCallback(id => {
    const state = store.getState();
    const profile = state.profiles[state.activeProfile];
    return profileCanEquip(profile, slot, state.items[id] || presetItems[id], merc);
  }, [store, slot, merc]);
}

function getProfileItem(profile, {location, slot}) {
  switch (location) {
  case "equipment": return profile.items[slot];
  case "mercenary": return profile.mercItems[slot];
  case "inventory": return profile.inventory[slot];
  case "cube": return profile.cube[slot];
  case "library": return slot;
  // no default
  }
}
function getProfileItemUnder(profile, {location, slot}, id, state) {
  switch (location) {
  case "equipment": return profile.items[slot];
  case "mercenary": return profile.mercItems[slot];
  case "inventory": return profile.inventory[inventoryFindIntersection(profile.inventory, slot, id, state, Data.info.inventory)];
  case "cube": return profile.cube[inventoryFindIntersection(profile.cube, slot, id, state, Data.info.cube)];
  // no default
  }
}
function setProfileItem(profile, {location, slot}, id, state, approx) {
  switch (location) {
  case "equipment":
    if (id) {
      if (profileCanEquip(profile, slot, state.items[id] || presetItems[id])) {
        return profileEquipItem(profile, slot, id, state);
      } else {
        return profile;
      }
    } else {
      return profileEquipItem(profile, slot, null, state);
    }
  case "mercenary":
    if (id) {
      if (profileCanEquip(profile, slot, state.items[id] || presetItems[id], true)) {
        return profileEquipMerc(profile, slot, id, state);
      } else {
        return profile;
      }
    } else {
      return profileEquipMerc(profile, slot, null, state);
    }
  case "inventory":
  case "cube":
    if (inventoryHasDuplicate(profile.inventory, id, state) || inventoryHasDuplicate(profile.cube, id, state)) {
      return profile;
    }
    if (approx) {
      const nearest = inventoryFindNearest(profile[location], slot, id, state, Data.info[location]);
      if (nearest >= 0 && !profile[location][nearest]) {
        profile = {...profile};
        profile[location] = profile[location].slice();
        profile[location][nearest] = id;
        return profile;
      }
    } else {
      const result = inventoryEquipItem(profile[location], slot, id, state, Data.info[location]);
      if (result !== profile[location]) {
        profile = {...profile, [location]: result};
      }
    }
    return profile;
  default:
    return profile;
  }
}

export function useItemMover() {
  const dispatch = Store.useDispatch();

  return React.useCallback((source, destination) => {
    if (!destination) return;
    if (source.location === destination.location && source.slot === destination.slot) return;
    const isCopy = destination.dropEffect === 'copy';

    dispatch.setProfile((profile, state) => {
      const item = getProfileItem(profile, source);
      if (!item) return profile;
      if (!isCopy) profile = setProfileItem(profile, source, null, state, false);
      const otherItem = getProfileItemUnder(profile, destination, item, state);
      profile = setProfileItem(profile, destination, item, state, false);
      if (!isCopy && otherItem) profile = setProfileItem(profile, source, otherItem, state, true);
      return profile;
    });
  }, [dispatch]);
}
